import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { sleep } from '@chainflip/utils/async';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { Link } from '@/shared/components/atoms/Link';
import { DashboardModalWithCloseButton } from '@/shared/components/dashboards/Modal';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import TokenSelectDropdown from '@/shared/components/dropdown/TokenSelectDropdown';
import Pill, { type PillColor } from '@/shared/components/flip-ui-kit/Pill';
import TokenInputAmount from '@/shared/components/input/TokenInputAmount';
import { IncentivisedApyText } from '@/shared/components/lending/IncentivisedApyText';
import SupplyRewardsTooltip, {
  type RewardsTooltipProps,
} from '@/shared/components/lending/SupplyRewardsTooltip';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { useIncentives } from '@/shared/hooks/useIncentives';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import { type LoanRiskType, useLoanRiskType } from '@/shared/hooks/useLoanRiskType';
import { useLpLendingPositions } from '@/shared/hooks/useLpLendingPositions';
import { useOraclePrice } from '@/shared/hooks/useOraclePrice';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import ArrowIconLarge from '@/shared/icons/flip-ui-kit/large/Arrow';
import ArrowIconSmall from '@/shared/icons/flip-ui-kit/small/Arrow';
import LockUnlocked from '@/shared/icons/flip-ui-kit/small/LockUnlocked';
import PercentageIcon from '@/shared/icons/flip-ui-kit/small/Percentage';
import SliceIcon from '@/shared/icons/flip-ui-kit/small/Slice';
import { apyToText } from '@/shared/utils/boost';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap } from '@/shared/utils/env';
import { calculateLowLtvPenaltyPercent, LENDING_POOLS_APY_QUERY } from '@/shared/utils/lending';
import {
  formatUsdValue,
  percentToPips,
  pipsToPercent,
  pipsToPercentString,
} from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import useLoanAccount from 'packages/liquidity-provision/hooks/useLoanAccount';
import {
  useExtrinsicSDK,
  useStateChainAccount,
} from 'packages/liquidity-provision/hooks/useStateChainAccount';
import SuccessIllustration from '../../../public/success-illustration.svg';

type PositionInfoBoxProps = {
  selectedToken: ChainflipToken;
  currentPosition: TokenAmount;
  projectedPosition: TokenAmount | undefined;
  borrowCapacityValueUsd: number;
  projectedBorrowCapacityValueUsd?: number;
  isTokenSelectOpen: boolean;
  rewardsData?: RewardsTooltipProps;
  currentPoolSharePercent: number;
  projectedPoolSharePercent?: number;
  baseApy: number;
  loanToValueBps: number;
  projectedLoanToValuePercent?: number;
  totalCollateralBalanceUsd: number;
  projectedCollateralBalanceUsd?: number;
  riskType: LoanRiskType;
  riskColor: PillColor;
  collateralValueLockedUsd: number;
  withdrawableValueUsd: number;
  projectedWithdrawableValueUsd?: number;
  penaltyFeePercentDiff: number;
};

type PositionInfoBoxSuccessProps = {
  selectedToken: ChainflipToken;
  currentPosition: TokenAmount;
  projectedPosition: TokenAmount;
  borrowCapacityValueUsd: number;
  projectedBorrowCapacityValueUsd: number;
  rewardsData?: RewardsTooltipProps;
  currentPoolSharePercent: number;
  projectedPoolSharePercent: number;
  baseApy: number;
};

const Success = ({
  asset,
  amount,
  children,
  amountUsd,
}: {
  asset: ChainflipAsset;
  amount: TokenAmount;
  children: React.ReactNode;
  amountUsd: number;
}) => (
  <div className="flex flex-col gap-6 rounded-md">
    <div className="my-auto flex w-full flex-col items-center justify-center">
      <div className="mb-[-74px]">
        <SuccessIllustration />
      </div>
      <div className="flex flex-col items-center gap-y-2 font-aeonikMedium">
        <span className="text-24 text-cf-white">Funds removed</span>
        <span className="flex items-center gap-x-1">
          {amount.toFormatted()}
          <TokenWithChainByAsset asset={asset} />
          {chainflipAssetMap[asset].symbol}
          <span className="text-cf-light-2">({formatUsdValue(amountUsd)})</span>
        </span>
        is now available in your Balance
      </div>
    </div>
    {children}
  </div>
);

const PositionInfoBox = ({
  selectedToken,
  currentPosition,
  projectedPosition,
  loanToValueBps,
  projectedLoanToValuePercent,
  totalCollateralBalanceUsd,
  projectedCollateralBalanceUsd,
  riskType,
  riskColor,
  collateralValueLockedUsd,
  withdrawableValueUsd,
  projectedWithdrawableValueUsd,
  borrowCapacityValueUsd,
  projectedBorrowCapacityValueUsd,
  currentPoolSharePercent,
  projectedPoolSharePercent,
  baseApy,
  isTokenSelectOpen,
  rewardsData,
  penaltyFeePercentDiff,
}: PositionInfoBoxProps) => (
  <>
    <div
      className={classNames(
        'flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium transition duration-100 ease-in',
        isTokenSelectOpen && 'opacity-50',
      )}
    >
      <div className="flex items-center justify-between text-14 text-cf-light-2">
        Position Size
        <div className="flex items-center gap-x-1 text-16">
          <span className={classNames(isNullish(projectedPosition) && 'text-cf-white')}>
            {currentPosition.toFormatted()} {selectedToken.symbol}
          </span>
          {projectedPosition && (
            <>
              <ArrowIconLarge />
              <span className="text-cf-white">
                {projectedPosition.toFormatted()} {selectedToken.symbol}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between text-14 text-cf-light-2">
        <div className="flex items-center gap-x-1">
          Loan-to-Value (LTV)
          <QuestionMarkTooltip content="The ratio between the value of your loan and the value of your collateral. A higher LTV means more borrowing power but also higher liquidation risk." />
        </div>
        <div className="flex items-center gap-x-1 text-16">
          <span className={classNames(isNullish(projectedLoanToValuePercent) && 'text-cf-white')}>
            {pipsToPercentString(loanToValueBps)}
          </span>
          {isNotNullish(projectedLoanToValuePercent) && (
            <>
              <ArrowIconLarge />
              <span className="text-cf-white">{apyToText(projectedLoanToValuePercent)}</span>
              <Pill color={riskColor} text={riskType} />
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-y-2 text-cf-light-2">
        <div className="flex items-center justify-between text-14">
          <div className="flex items-center gap-x-1">
            Collateral Value
            <QuestionMarkTooltip content="Total USD value of all assets you've supplied as collateral across all lending pools." />
          </div>
          <div className="flex items-center gap-x-1 text-16">
            <span
              className={classNames(isNullish(projectedCollateralBalanceUsd) && 'text-cf-white')}
            >
              {formatUsdValue(totalCollateralBalanceUsd)}
            </span>
            {isNotNullish(projectedCollateralBalanceUsd) && (
              <>
                <ArrowIconLarge />
                <span className="text-cf-white">
                  {formatUsdValue(projectedCollateralBalanceUsd)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between pl-2 text-12">
          <div className="flex items-center gap-x-1">
            <LockUnlocked />
            Withdrawable
            <QuestionMarkTooltip content="The portion of your supplied assets not locked against an active loan." />
          </div>
          <div className="flex items-center gap-x-1">
            <span
              className={classNames(isNullish(projectedWithdrawableValueUsd) && 'text-cf-white')}
            >
              {formatUsdValue(withdrawableValueUsd)}
            </span>
            {isNotNullish(projectedWithdrawableValueUsd) && (
              <>
                <ArrowIconLarge />
                <span className="text-cf-white">
                  {formatUsdValue(projectedWithdrawableValueUsd)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between pl-2 text-12">
          <div className="flex items-center gap-x-1">
            <LockUnlocked />
            Locked as Collateral
            <QuestionMarkTooltip content="The portion of your supplied assets locked against your active loans. It cannot be withdrawn until the loan is repaid." />
          </div>
          <div className="flex items-center gap-x-1">
            <span className="text-cf-white">{formatUsdValue(collateralValueLockedUsd)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between text-14 text-cf-light-2">
        <div className="flex items-center gap-x-1">
          Borrow Capacity
          <QuestionMarkTooltip content="The maximum amount you can borrow based on the value of your supplied assets and outstanding loans." />
        </div>
        <div className="flex items-center gap-x-1 text-16">
          <span
            className={classNames(isNullish(projectedBorrowCapacityValueUsd) && 'text-cf-white')}
          >
            {formatUsdValue(borrowCapacityValueUsd)}
          </span>
          {isNotNullish(projectedBorrowCapacityValueUsd) && (
            <>
              <ArrowIconLarge />
              <span className="text-cf-white">
                {formatUsdValue(projectedBorrowCapacityValueUsd)}
              </span>
            </>
          )}
        </div>
      </div>
      {penaltyFeePercentDiff >= 0.001 && (
        <div className="flex flex-wrap items-center gap-x-1 text-12 text-cf-light-2">
          Withdrawing supply reduces your loan APR by {penaltyFeePercentDiff.toFixed(3)}%
          <div>
            (
            <Link
              href="https://docs.chainflip.io/lending/fees#low-ltv-penalty"
              target="_blank"
              className="text-cf-light-2"
              underline
            >
              Learn more
            </Link>
            )
          </div>
        </div>
      )}
    </div>
    <div
      className={classNames(
        'flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2 transition duration-100 ease-in',
        isTokenSelectOpen && 'opacity-50',
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <PercentageIcon />
          Projected APY
          <QuestionMarkTooltip content="The estimated annual yield you'll earn, before and after removing this amount." />
        </div>
        <div className="flex items-center gap-x-2 text-cf-white">
          {rewardsData ? (
            <IncentivisedApyText
              tooltipContent={<SupplyRewardsTooltip {...rewardsData} />}
              totalApy={rewardsData.baseApy + rewardsData.rewardsApy}
              baseApy={rewardsData.baseApy}
              rewardsApy={rewardsData.rewardsApy}
              className="w-auto"
            />
          ) : (
            <span className={classNames(projectedPosition?.eq(0) && 'text-cf-light-2')}>
              {apyToText(baseApy)}
            </span>
          )}
          {projectedPosition?.eq(0) && (
            <>
              <ArrowIconSmall className="text-cf-light-2" />
              <span>{apyToText(0)}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <SliceIcon />
          {isNotNullish(projectedPoolSharePercent) && 'Projected '}Pool Share
        </div>
        <div className="flex items-center gap-x-2 text-cf-white">
          <span
            className={classNames(isNotNullish(projectedPoolSharePercent) && 'text-cf-light-2')}
          >
            {currentPoolSharePercent.toFixed(2)}%
          </span>
          {isNotNullish(projectedPoolSharePercent) && (
            <>
              <ArrowIconSmall className="text-cf-light-2" />
              <span>{projectedPoolSharePercent.toFixed(2)}%</span>
            </>
          )}
        </div>
      </div>
    </div>
  </>
);

const PositionInfoBoxSuccess = ({
  selectedToken,
  currentPosition,
  projectedPosition,
  borrowCapacityValueUsd,
  projectedBorrowCapacityValueUsd,
  currentPoolSharePercent,
  projectedPoolSharePercent,
  baseApy,
  rewardsData,
}: PositionInfoBoxSuccessProps) => (
  <>
    <div className="flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium transition duration-100 ease-in">
      <div className="flex flex-col gap-y-2 text-14 text-cf-light-2">
        Position Size
        <div className="flex items-center gap-x-1 text-16">
          <span className="text-cf-light-2">
            {currentPosition.toFormatted()} {selectedToken.symbol}
          </span>
          <ArrowIconLarge />
          <span className="text-cf-white">
            {projectedPosition.toFormatted()} {selectedToken.symbol}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-y-2 text-14 text-cf-light-2">
        <div className="flex items-center gap-x-1">
          Borrow Capacity
          <QuestionMarkTooltip content="The maximum amount you can borrow based on the value of your supplied assets and outstanding loans." />
        </div>
        <div className="flex items-center gap-x-1 text-16">
          <span className="text-cf-light-2">{formatUsdValue(borrowCapacityValueUsd)}</span>
          <ArrowIconLarge />
          <span className="text-cf-white">{formatUsdValue(projectedBorrowCapacityValueUsd)}</span>
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2 transition duration-100 ease-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <PercentageIcon />
          Projected APY
          <QuestionMarkTooltip content="The estimated annual yield you'll earn, before and after removing this amount." />
        </div>
        <div className="flex items-center gap-x-2 text-cf-white">
          {rewardsData ? (
            <IncentivisedApyText
              tooltipContent={<SupplyRewardsTooltip {...rewardsData} />}
              totalApy={rewardsData.baseApy + rewardsData.rewardsApy}
              baseApy={rewardsData.baseApy}
              rewardsApy={rewardsData.rewardsApy}
              className="w-auto"
            />
          ) : (
            <span className={classNames(projectedPosition.eq(0) && 'text-cf-light-2')}>
              {apyToText(baseApy)}
            </span>
          )}
          {projectedPosition.eq(0) && (
            <>
              <ArrowIconSmall className="text-cf-light-2" />
              <span>{apyToText(0)}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <SliceIcon />
          Projected Pool Share
        </div>
        <div className="flex items-center gap-x-2 text-cf-white">
          <span className="text-cf-light-2">{currentPoolSharePercent.toFixed(2)}%</span>
          <ArrowIconSmall className="text-cf-light-2" />
          <span>{projectedPoolSharePercent.toFixed(2)}%</span>
        </div>
      </div>
    </div>
  </>
);

interface RemoveSupplyModalProps {
  open: boolean;
  onClose: () => void;
  selectedToken: ChainflipToken;
  setSelectedToken: (token: ChainflipToken) => void;
}

export const RemoveSupplyModal = ({
  open,
  onClose,
  selectedToken,
  setSelectedToken,
}: RemoveSupplyModalProps): JSX.Element => {
  const { selectedAccount } = usePolkadot(true);
  const { refreshAccountInfo } = useStateChainAccount();
  const focusRef = useRef<HTMLButtonElement>(null);
  const [amount, setAmount] = useState<TokenAmount | undefined>(undefined);
  const { toast, sendExtrinsic } = useExtrinsicSDK('removeLenderFunds');
  const router = useRouter();
  const [prevValues, setPrevValues] = useState<{
    amount: TokenAmount | undefined;
    position: TokenAmount;
    apy: number;
    poolSharePercent: number;
    borrowCapacityUsd: number;
  }>();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | undefined>(undefined);
  const [emptyAmountError, setEmptyAmountError] = useState<string>();
  const [isTokenSelectOpen, setIsTokenSelectOpen] = useState(false);
  const [isMaxSelected, setIsMaxSelected] = useState(false);
  const [systemRevision, setSystemRevision] = useState(0);

  const { activeSeason } = useIncentives();
  const supplyIncentive = activeSeason?.pools?.find(
    (incentive) => incentive.asset === selectedToken.chainflipId && incentive.type === 'SUPPLY',
  );

  const price = useOraclePrice(selectedToken.chainflipId);

  const {
    minSupplyAmountUsd,
    targetLtvThresholdBps,
    lowLtvPenaltyThresholdBps,
    lowLtvPenaltyBpsAtMaxLtv,
  } = useLendingConfig();

  const { loanAccount, penaltyFeePercent: currentPenaltyFeePercent } = useLoanAccount();
  const { loanToValueBps, borrowPowerUsedBps, totalBorrowAmountUsd, totalCollateralBalanceUsd } =
    loanAccount;

  const { poolAssets, poolBalances } = useLendingPools();
  const { lpPositions } = useLpLendingPositions({
    idSs58: selectedAccount.idSs58,
  });

  const tokensWithPositions = poolAssets.map((asset) => ({
    token: chainflipAssetMap[asset],
    balance: lpPositions?.[asset]?.totalAvailableAmount ?? TokenAmount.ZERO,
  }));

  const selectedTokenPosition =
    lpPositions?.[selectedToken?.chainflipId]?.totalAmount ?? TokenAmount.ZERO;

  const poolSharePercent = lpPositions?.[selectedToken.chainflipId]?.poolSharePercent ?? 0;

  const baseApy = lpPositions?.[selectedToken.chainflipId]?.projectedApy ?? 0;

  const currentPoolTotalAmount =
    poolBalances?.[selectedToken.chainflipId]?.totalAmount ?? TokenAmount.ZERO;
  const currentPoolTotalAvailableAmount =
    poolBalances?.[selectedToken.chainflipId]?.totalAvailableAmount ?? TokenAmount.ZERO;

  // Handles dust amount projections by showing a 0 instead of <0.0000001
  const projectedPositionRaw = selectedTokenPosition.saturatingSub(amount ?? TokenAmount.ZERO);
  const projectedPosition =
    projectedPositionRaw.toUsdNumber(price) < 0.01 && isMaxSelected
      ? TokenAmount.ZERO
      : projectedPositionRaw;

  const projectedPoolSharePercent = currentPoolTotalAmount
    .saturatingSub(amount ?? TokenAmount.ZERO)
    .eq(0)
    ? 0
    : projectedPosition
        .ratio(currentPoolTotalAmount.saturatingSub(amount ?? TokenAmount.ZERO))
        .multipliedBy(100)
        .toNumber();

  const amountToUsd = (amount ?? TokenAmount.ZERO).toUsdNumber(price);
  const projectedCollateralBalanceUsd = totalCollateralBalanceUsd - amountToUsd;
  const projectedLoanToValuePercent =
    // eslint-disable-next-line no-nested-ternary
    projectedCollateralBalanceUsd > 0
      ? Math.min(
          100,
          Math.floor((totalBorrowAmountUsd / projectedCollateralBalanceUsd) * 100 * 100) / 100,
        ) // truncate to 2 decimal places without rounding
      : totalBorrowAmountUsd > 0
        ? 100 // if projected collateral is zero - it's liquidation
        : 0;

  const collateralValueLockedUsd =
    (pipsToPercent(borrowPowerUsedBps) * totalCollateralBalanceUsd) / 100;
  const withdrawableValueUsd = Math.max(0, totalCollateralBalanceUsd - collateralValueLockedUsd);
  const projectedWithdrawableValueUsd = Math.max(0, withdrawableValueUsd - amountToUsd);

  const availableToWithdrawUsd = Math.min(
    withdrawableValueUsd,
    selectedTokenPosition.toUsdNumber(price),
  );

  const selectedTokenAvailableToWithdraw = useMemo(
    () =>
      TokenAmount.fromWholeUnits(
        price ? availableToWithdrawUsd / price : 0,
        selectedToken.decimals,
      ),
    [availableToWithdrawUsd, price, selectedToken.decimals],
  );

  const borrowCapacityUsd = Math.max(
    (totalCollateralBalanceUsd * (targetLtvThresholdBps ?? 0)) / 10_000 - totalBorrowAmountUsd,
    0,
  );
  const projectedBorrowCapacityUsd = Math.max(
    (projectedCollateralBalanceUsd * (targetLtvThresholdBps ?? 0)) / 10_000 - totalBorrowAmountUsd,
    0,
  );

  const rewardsData = supplyIncentive
    ? {
        baseApy,
        rewardsApy: supplyIncentive.apy,
        seasonIndex: activeSeason?.seasonIndex ?? 0,
        dailyPoints: supplyIncentive.dailyPoints,
      }
    : undefined;

  const { riskType, riskColor } = useLoanRiskType(
    isNotNullish(projectedLoanToValuePercent)
      ? percentToPips(projectedLoanToValuePercent)
      : loanToValueBps,
  );

  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useMutation({
    mutationFn: async () => {
      setPrevValues({
        amount,
        position: selectedTokenPosition,
        apy: baseApy,
        poolSharePercent: Number(poolSharePercent),
        borrowCapacityUsd,
      });

      return sendExtrinsic(
        selectedToken.chainflipId,
        isMaxSelected ? undefined : amount?.toString(),
      );
    },
    async onSuccess() {
      await sleep(500);
      await Promise.all(
        [LENDING_POOLS_APY_QUERY, 'cf_lending_pools', 'cf_account_info'].map((query) =>
          queryClient.invalidateQueries({ queryKey: [query], exact: false }),
        ),
      );
      await refreshAccountInfo();
      toast.success(
        `Liquidity was removed from ${chainflipAssetMap[selectedToken.chainflipId].symbol} lending pool`,
      );
    },
    onError(err) {
      setPrevValues(undefined);
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  const ctaClickHandler = () => {
    if (!amount || amount.lte(0)) {
      setEmptyAmountError('Please enter a valid amount');
      return;
    }
    if (amount.gt(currentPoolTotalAvailableAmount)) {
      setError('Amount exceeds available liquidity in the pool. Please enter smaller amount');
      return;
    }
    if (projectedPosition.gt(0) && !isMaxSelected) {
      if (amount.toUsdNumber(price) < minSupplyAmountUsd) {
        setError(
          `Please request an amount higher than the minimum removal amount: $${minSupplyAmountUsd}`,
        );
        return;
      }
      if (projectedPosition.toUsdNumber(price) < minSupplyAmountUsd) {
        setError(
          `Outstanding position size should be greater than the minimum supply amount: $${minSupplyAmountUsd}`,
        );
        return;
      }
    }
    mutate();
  };

  const isProjectedValueDisabled =
    !amount ||
    amount.eq(0) ||
    (isPending && prevValues && selectedTokenPosition.lt(prevValues.position));

  // Removing collateral raises the LTV, which can lower the low-LTV penalty fee
  const projectedPenaltyFeePercent = calculateLowLtvPenaltyPercent(
    projectedLoanToValuePercent,
    pipsToPercent(lowLtvPenaltyThresholdBps),
    pipsToPercent(lowLtvPenaltyBpsAtMaxLtv),
  );

  // Absolute change between the current and projected low-LTV penalty fee
  const penaltyFeePercentDiff = isProjectedValueDisabled
    ? 0
    : Math.abs(projectedPenaltyFeePercent - currentPenaltyFeePercent);

  const handleTokenInput = useCallback(
    (value: TokenAmount | undefined) => {
      setAmount(value);
      if (!value?.eq(selectedTokenAvailableToWithdraw)) setIsMaxSelected(false);
      if (value?.gt(0)) setEmptyAmountError(undefined);
    },
    [selectedTokenAvailableToWithdraw],
  );

  // Sync input value with max available if user selected max
  useEffect(() => {
    if (isMaxSelected) {
      setAmount(selectedTokenAvailableToWithdraw);
      setSystemRevision((v) => v + 1);
      setEmptyAmountError(undefined);
    }
  }, [isMaxSelected, selectedTokenAvailableToWithdraw]);

  return (
    <DashboardModalWithCloseButton
      initialFocus={focusRef}
      open={open}
      onClose={onClose}
      afterLeave={() => {
        setAmount(undefined);
        setPrevValues(undefined);
        setError('');
        setEmptyAmountError(undefined);
        setIsMaxSelected(false);
        resetMutation();
      }}
    >
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div key="supply-remove-success" {...defaultAnimationProps}>
            <Success
              asset={selectedToken.chainflipId}
              amount={prevValues?.amount ?? TokenAmount.ZERO}
              amountUsd={(prevValues?.amount ?? TokenAmount.ZERO).toUsdNumber(price)}
            >
              <PositionInfoBoxSuccess
                selectedToken={selectedToken}
                currentPosition={prevValues?.position ?? TokenAmount.ZERO}
                projectedPosition={selectedTokenPosition}
                currentPoolSharePercent={prevValues?.poolSharePercent ?? 0}
                projectedPoolSharePercent={poolSharePercent}
                baseApy={prevValues?.apy ?? 0}
                rewardsData={rewardsData}
                borrowCapacityValueUsd={prevValues?.borrowCapacityUsd ?? 0}
                projectedBorrowCapacityValueUsd={borrowCapacityUsd}
              />
              <ReadonlyButton
                className="w-full"
                type="primary-standard"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  return router.push('/lending?tab=supply');
                }}
              >
                View my Position
              </ReadonlyButton>
            </Success>
          </motion.div>
        ) : (
          <motion.div key="remove-supply-form" {...defaultAnimationProps}>
            <div className="flex flex-col gap-y-6">
              <div className="font-aeonikMedium text-16 text-cf-white">Withdraw Supplied Funds</div>
              <div className="h-px bg-cf-gray-4" />
              <TokenSelectDropdown
                isOpen={isTokenSelectOpen}
                setIsOpen={setIsTokenSelectOpen}
                selectedToken={selectedToken}
                setSelectedToken={(token) => {
                  setAmount(undefined);
                  setError('');
                  setEmptyAmountError(undefined);
                  setIsMaxSelected(false);
                  setSelectedToken(token);
                }}
                tokensWithBalances={tokensWithPositions}
                disabled={isPending}
              />
              <TokenInputAmount
                key={`${selectedToken.chainflipId}-${systemRevision}`}
                label="Amount"
                token={selectedToken}
                balance={selectedTokenAvailableToWithdraw}
                value={amount}
                onChange={handleTokenInput}
                onClickMax={() => setIsMaxSelected(true)}
                disabled={!selectedToken || isPending}
                focusOnMount
                balanceLabel="Available to withdraw"
                error={error || emptyAmountError}
                setError={setError}
                maxErrorMessage="Amount exceeds available amount"
                maxUpperLimit={currentPoolTotalAvailableAmount}
                showError={!isPending}
              />
              <PositionInfoBox
                selectedToken={selectedToken}
                currentPosition={selectedTokenPosition}
                projectedPosition={isProjectedValueDisabled ? undefined : projectedPosition}
                loanToValueBps={loanToValueBps}
                projectedLoanToValuePercent={
                  isProjectedValueDisabled ? undefined : projectedLoanToValuePercent
                }
                riskType={riskType}
                riskColor={riskColor}
                totalCollateralBalanceUsd={totalCollateralBalanceUsd}
                projectedCollateralBalanceUsd={
                  isProjectedValueDisabled ? undefined : projectedCollateralBalanceUsd
                }
                collateralValueLockedUsd={collateralValueLockedUsd}
                withdrawableValueUsd={withdrawableValueUsd}
                projectedWithdrawableValueUsd={
                  isProjectedValueDisabled ? undefined : projectedWithdrawableValueUsd
                }
                borrowCapacityValueUsd={borrowCapacityUsd}
                projectedBorrowCapacityValueUsd={
                  isProjectedValueDisabled ? undefined : projectedBorrowCapacityUsd
                }
                currentPoolSharePercent={poolSharePercent}
                projectedPoolSharePercent={
                  isProjectedValueDisabled ? undefined : projectedPoolSharePercent
                }
                baseApy={baseApy}
                isTokenSelectOpen={isTokenSelectOpen}
                rewardsData={rewardsData}
                penaltyFeePercentDiff={penaltyFeePercentDiff}
              />
              <ReadonlyButton
                type="primary-standard"
                fullWidth
                onClick={ctaClickHandler}
                loading={isPending && 'iconOnly'}
                disabled={Boolean(emptyAmountError) || Boolean(error) || isPending}
              >
                Remove Funds
              </ReadonlyButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardModalWithCloseButton>
  );
};
